import React from 'react';

import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import DocumentActions from 'notebook/documents/DocumentActions';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import {WorksheetStepFrameProps} from 'notebook/worksheets/steps/WorksheetStepFrame';

import {
  StatusTypeToDisplayNameMapping,
  WORKSHEET_STEP_LOADING_STATUSES,
  WorksheetTaskStatusType,
} from '../WorksheetConstants';

const TERMINAL_STATES = [
  WorksheetTaskStatusType.COMPLETED,
  WorksheetTaskStatusType.SKIPPED,
  WorksheetTaskStatusType.FAILED,
];

const REVIEW_STATUS_ACCEPTED_STATES = [ReviewStatus.ACCEPTED, ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS];

export interface BannerMessageProps extends WorksheetStepFrameProps {
  isLatest?: boolean;
  latestVersionId?: number;
}

interface LatestVersionLinkProps {
  documentId: string;
  latestVersionId: number;
}

function LatestVersionLink({documentId, latestVersionId}: LatestVersionLinkProps): JSX.Element {
  const handleClick = (): void => {
    DocumentActions.loadVersion(documentId, latestVersionId);
  };
  return <a onClick={handleClick}>View the latest version.</a>;
}

export default function BannerMessage(props: BannerMessageProps): JSX.Element | null {
  const {status} = props.stepTaskData;
  if (props.worksheetReviewStatus && ReviewConstants.LOCKED_STATUSES.includes(props.worksheetReviewStatus)) {
    const isAccepted = REVIEW_STATUS_ACCEPTED_STATES.includes(props.worksheetReviewStatus);
    const message = isAccepted
      ? 'Editing is disabled because the worksheet has been reviewed.'
      : 'Editing is disabled because the worksheet is in review.';
    return (
      <span>
        {message}{' '}
        {props.isLatest === false && props.latestVersionId && (
          <span>
            You're viewing a previous step version.{' '}
            <LatestVersionLink documentId={props.document.id} latestVersionId={props.latestVersionId} />
          </span>
        )}
      </span>
    );
  }
  if (status && WORKSHEET_STEP_LOADING_STATUSES.includes(status)) {
    return <span>Editing is disabled because step is updating.</span>;
  } else if (props.isSelectedStepActionsLocked) {
    return (
      <span>
        Editing is disabled because the run has not started. <strong>Start run</strong> to unlock the Run
        steps.
      </span>
    );
  } else if (status && TERMINAL_STATES.includes(status)) {
    const statusText = StatusTypeToDisplayNameMapping[status].toLowerCase();
    return (
      <span>
        Editing is disabled because step has been <strong>{statusText}</strong>.{' '}
        {props.isLatest === false && props.latestVersionId && (
          <span>
            You're viewing a previous step version.{' '}
            <LatestVersionLink documentId={props.document.id} latestVersionId={props.latestVersionId} />
          </span>
        )}
      </span>
    );
  } else if (props.isLatest === false && props.latestVersionId) {
    return (
      <span>
        Editing is disabled because you're viewing a previous step version.{' '}
        <LatestVersionLink documentId={props.document.id} latestVersionId={props.latestVersionId} />
      </span>
    );
  } else if (props.stepTaskData.status === WorksheetTaskStatusType.NOT_READY) {
    return <span>Editing is disabled because the previous unit operation has not been completed.</span>;
  }

  return null;
}
